// /app/frontend/src/pages/seo/EthicalPolyamoryForBeginnersPage.jsx
// Focus: EDUCATIONAL 101 page with terminology + FAQ schema for rich snippets.
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  buildFaqSchema,
  buildBreadcrumbSchema,
  GradientHero,
  FaqList,
  RelatedGuides,
  BottomCTA,
} from './SEOUtils';

const FAQS = [
  {
    q: 'What is ethical polyamory?',
    a: 'Ethical polyamory is the practice of having multiple romantic relationships at the same time, with the full knowledge, consent, and ongoing communication of everyone involved. The word ethical is doing a lot of work in that sentence — without consent and transparency, it is not polyamory; it is infidelity.',
  },
  {
    q: 'Is polyamory the same as cheating?',
    a: 'No. Cheating happens when someone breaks the agreements of their relationship. Polyamory is a relationship style where the agreement includes multiple partners. The distinction is consent. A polyamorous person who lies to one of their partners about another partner is still cheating; their relationship structure does not make them immune to broken trust.',
  },
  {
    q: 'How is polyamory different from open relationships?',
    a: 'Open relationships are usually sexually non-monogamous while keeping romantic exclusivity. Polyamory typically includes the possibility of multiple loving, romantic, committed relationships. Both fall under the broader umbrella of ethical non-monogamy, and many people mix elements of both.',
  },
  {
    q: 'Can polyamorous relationships actually last?',
    a: 'Yes. Long-term polyamorous relationships are common, including ones lasting decades, raising children, and building lifelong family structures. The factors that make any relationship last — communication, trust, mutual care, shared values — apply just as much to polyamory; there are simply more relationships to maintain.',
  },
  {
    q: 'What if I get jealous?',
    a: 'Jealousy is normal in polyamory, not a sign that you are doing it wrong. Healthy polyamory does not pretend jealousy does not exist; it gives people tools to name it, sit with it, and work through it together. Most polyamorous people will tell you jealousy lessens with practice and trust — but it never disappears entirely, and that is okay.',
  },
  {
    q: 'Where do I start?',
    a: 'Start with reading, conversations, and self-reflection — not with adding a new partner. The single most predictive factor of healthy polyamory is whether each adult has done their own emotional work first. Communication tools, books, therapists who understand polyamory, and communities like UnityLove are all good starting points.',
  },
];

export default function EthicalPolyamoryForBeginnersPage() {
  const path = '/ethical-polyamory-for-beginners';
  const schemas = [
    organizationSchema,
    buildFaqSchema(FAQS),
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: '/guides' },
      { name: 'Ethical Polyamory For Beginners', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Ethical Polyamory For Beginners"
        description="Learn the foundations of ethical polyamory, communication, boundaries, and healthy relationship structures."
        canonical={path}
        schemas={schemas}
      />

      <GradientHero
        eyebrow="A UnityLove Guide"
        headline="Understanding Ethical Polyamory"
        sub="Polyamory in plain language — what it is, what it is not, and how the strongest poly relationships actually work."
        body="If you are new to ethical polyamory or curious about whether it might fit your life, this guide covers the foundations without jargon."
        primaryCta={{ label: 'Create Your Profile', to: '/auth?mode=signup' }}
        secondaryCta={{ label: 'Safe Poly Dating Guide', to: '/safe-poly-dating' }}
      />

      <main className="max-w-3xl mx-auto px-4 py-14">
        {/* What it is */}
        <section className="mb-12">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-3">
            What Polyamory Actually Is
          </h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <p>
              Polyamory is the practice of having more than one romantic relationship at the same time, with the full knowledge and consent of everyone involved. The word comes from the Greek and Latin roots for many loves. At its center is a simple idea: love is not a finite resource, and a person can genuinely care for more than one partner without one taking away from the other.
            </p>
            <p>
              Ethical polyamory is what most modern poly communities mean when they say polyamory — emphasizing consent, transparency, communication, and care for everyone in the network of relationships.
            </p>
          </div>
        </section>

        {/* Terminology */}
        <section className="mb-12">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-5">
            A Quick Glossary
          </h2>
          <dl className="space-y-4">
            {[
              ['Metamour', 'Your partner\'s other partner. Someone you may or may not be romantically involved with, but who is part of your relationship network.'],
              ['Compersion', 'The joy you feel when a partner is happy with someone else. Often described as the opposite of jealousy.'],
              ['Hierarchy', 'A structure where one relationship is treated as primary, with additional partners considered secondary. Common, but not universal in poly.'],
              ['Anchor partner', 'A long-term, central partner who shares life-defining infrastructure with you (often a household, finances, kids) without claiming the priority language of hierarchy.'],
              ['Kitchen-table poly', 'A style where all partners and metamours are comfortable sharing space and friendship.'],
              ['Parallel poly', 'A style where partners date their own people separately, with minimal overlap between the different relationships.'],
              ['Solo poly', 'A style where someone practices polyamory without seeking a primary partner or merged life — staying autonomous as the anchor.'],
              ['Triad / Throuple', 'Three people in a closed romantic relationship with each other.'],
              ['Polycule', 'The full network of romantic relationships connected through one or more shared partners.'],
            ].map(([term, def]) => (
              <div key={term} className="rounded-xl border border-slate-200 bg-white p-4">
                <dt className="font-semibold text-slate-900">{term}</dt>
                <dd className="text-slate-700 mt-1">{def}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Core principles */}
        <section className="mb-12 bg-slate-50 rounded-3xl p-6 sm:p-8">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
            The Four Core Principles
          </h2>
          <ol className="space-y-4 text-slate-700">
            <li>
              <strong className="text-slate-900">Consent.</strong> Everyone in the network agrees, knowingly, to the structure they are in. Surprise is not consent.
            </li>
            <li>
              <strong className="text-slate-900">Transparency.</strong> Information flows. Partners do not hide each other. The default is honesty, not selective disclosure.
            </li>
            <li>
              <strong className="text-slate-900">Communication.</strong> Hard conversations happen early and often, instead of being saved up. Most healthy poly people describe themselves as over-communicators.
            </li>
            <li>
              <strong className="text-slate-900">Care.</strong> Every partner is treated as a whole human, not a role. Comfort, time, dignity, and emotional safety apply to everyone — not only the primary relationship.
            </li>
          </ol>
        </section>

        {/* Common myths */}
        <section className="mb-12">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-4">
            Common Myths
          </h2>
          <div className="space-y-3">
            {[
              ['Myth: Polyamory is just an excuse to cheat.', 'Polyamory and cheating are opposites. Cheating involves deception; polyamory requires transparency.'],
              ['Myth: It is all about sex.', 'For most poly people, the emotional, romantic, and life-partnership aspects of multiple relationships matter far more than the sexual element.'],
              ['Myth: Poly people do not get jealous.', 'They do. They have just built better tools for handling it.'],
              ['Myth: One person must be the favorite.', 'In healthy poly, each relationship is its own thing, valued on its own terms — not ranked against the others.'],
              ['Myth: It is unstable.', 'Long-term polyamorous families, raising children, lasting decades, exist. Stability comes from the work, not from the structure.'],
            ].map(([myth, truth]) => (
              <div key={myth} className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="font-semibold text-slate-900">{myth}</p>
                <p className="text-slate-700 mt-1.5 text-sm">{truth}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-4">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-2">
            Frequently Asked Questions
          </h2>
          <p className="text-slate-600 mb-6">
            The questions we hear most often from people new to ethical polyamory.
          </p>
          <FaqList items={FAQS} />
        </section>
      </main>

      <RelatedGuides currentPath={path} />
      <BottomCTA />
    </div>
  );
}
